import React from "react";
import { Probable } from "@tvg/ts-types/Race";
import { Colors, Saddle } from "@tvg/design-system";
import { TableData } from "./types";
import { CornerCell } from "./styled-components";
import AmountDisplay from "./AmountDisplay";
import { RunnerBI } from "../../../hooks/useGetRunnerFromBI";

/**
 * Returns the settings of a runner by merging all runners with the same biNumber
 * @param position column or row number
 * @returns Info of the runner
 */
const pickRunnerInfo = (runners: RunnerBI[], position: number) =>
  runners.reduce((acc: RunnerBI, item: RunnerBI): RunnerBI => {
    if (item && item.biNumber === position) {
      let { isScratched } = item;

      // We want to make sure this value we are merging is not undefined because the initial value is undefined
      if (acc.isScratched !== undefined)
        isScratched = item.isScratched && acc.isScratched;

      return {
        ...item,
        isScratched
      };
    }

    return acc;
  }, {} as RunnerBI);

/**
 * Builds the data array for the table
 * @param probable probables from current race
 * @param runnerColors object with the colors matching each runner id on the race
 * @returns data array
 */
export const buildTableData = (
  probable: Probable | undefined,
  runners: RunnerBI[],
  nextRunners: RunnerBI[],
  colors: Colors,
  isMobile?: boolean
): TableData[] => {
  if (!probable) return [];

  // Default column and print empty cell for the corner
  const dataset: TableData[] = [
    {
      saddleStatic: (
        <CornerCell colors={colors}>
          <div />
        </CornerCell>
      )
    }
  ];
  // Get unique array of columns and rows id's
  const rows = new Set(probable.betCombos.map((combo) => combo.runner1 || ""));
  const columns = new Set(
    probable.betCombos.map((combo) => combo.runner2 || "-")
  );

  columns.forEach((col) => {
    rows.forEach((row) => {
      // Searches a combo that matches runner1/runner2 or if one of them is null and returns the payout
      const amount = probable.betCombos.find(
        (combo) =>
          (combo.runner1 === row && combo.runner2 === col) ||
          (combo.runner1 === row && combo.runner2 === null) ||
          (combo.runner1 === null && combo.runner2 === col)
      )?.payout;

      const colRunners = nextRunners.length > 0 ? nextRunners : runners;

      const colInfo = pickRunnerInfo(colRunners, +col);
      const rowInfo = pickRunnerInfo(runners, +row);

      // console.log(rowInfo);

      // Pushes the saddle icon associated to a runner to the first column of the dataset
      dataset[0] = {
        ...dataset[0],
        [`saddle${row}`]: (
          <Saddle
            size="s"
            number={row}
            saddleColor={rowInfo?.saddleColor}
            numberColor={rowInfo?.numberColor}
            isScratched={rowInfo?.isScratched}
            qaLabel={`probables-table-row-saddle-${row}`}
            key={`saddle-row-${row}`}
          />
        )
      };

      // Pushes the saddle icon associated to a runner to the first position of each column
      // and creates a key for the runner and gives it the amount as value
      dataset[col as unknown as number] = {
        ...dataset[col as unknown as number],
        saddleStatic: (
          <Saddle
            size="s"
            number={col}
            saddleColor={colInfo?.saddleColor}
            numberColor={colInfo?.numberColor}
            isScratched={colInfo?.isScratched}
            qaLabel={`probables-table-col-saddle-${col}`}
            key={`saddle-col-${col}`}
          />
        ),
        [`saddle${row}`]: (
          <>
            <AmountDisplay
              amount={
                colInfo?.isScratched ||
                rowInfo?.isScratched ||
                (amount?.toString() === "0" && isMobile)
                  ? undefined
                  : amount?.toString() // forcing toString() because backend is sending a string and typescript is expecting a number
              }
            />
          </>
        )
      };
    });
  });

  return dataset;
};
